import { useEffect, useState } from 'react';
import { Button, Drawer, Modal, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { markingApi, GtinMarkItem } from '../../api/marking';
import useIsMobile from '../../hooks/useIsMobile';

const { Text } = Typography;

interface Props {
  open: boolean;
  productId: string | null;
  documentId?: string | null;
  serviceOrderId?: number | null;
  onPick: (mark: GtinMarkItem) => void;
  onCancel: () => void;
}

const MarkPickerModal: React.FC<Props> = ({ open, productId, documentId, serviceOrderId, onPick, onCancel }) => {
  const isMobile = useIsMobile();
  const [marks, setMarks] = useState<GtinMarkItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !productId) return;
    setLoading(true);
    markingApi.getMarksByProduct(productId, documentId ?? undefined, serviceOrderId ?? undefined)
      .then(r => setMarks(r.data || []))
      .catch(e => {
        message.error(e?.response?.data?.detail || 'Не удалось загрузить марки товара');
        setMarks([]);
      })
      .finally(() => setLoading(false));
  }, [open, productId, documentId, serviceOrderId]);

  const content = (
    <Table<GtinMarkItem>
      size="small"
      rowKey="marking_code"
      loading={loading}
      dataSource={marks}
      pagination={false}
      locale={{ emptyText: 'Марки этого товара не найдены — отсканируйте бочку сканером' }}
      columns={[
        {
          title: 'Марка',
          key: 'code',
          render: (_, m) => (
            <span>
              <Text
                style={{ fontSize: 12, wordBreak: 'break-all', ...(m.has_full_code === false ? { color: '#ff4d4f' } : {}) }}
                title={m.has_full_code === false ? 'Марка без криптохвоста — отсканируйте бочку сканером, иначе заказ не сохранить' : undefined}
              >
                {m.marking_code}
              </Text>
              {m.has_full_code === false && (
                <>
                  <br />
                  <Tag color="red" style={{ marginTop: 4 }}>без криптохвоста — отсканируйте</Tag>
                </>
              )}
            </span>
          ),
        },
        {
          title: 'Дата приёма',
          key: 'received',
          width: 110,
          render: (_, m) => (m.received_at ? dayjs(m.received_at).format('DD.MM.YYYY') : '—'),
        },
        {
          title: 'Остаток',
          key: 'volume',
          width: 200,
          render: (_, m) => (
            <span>
              {m.available_volume != null ? `свободно ${Number(m.available_volume)} л` : '—'}
              {m.reserved_volume != null && Number(m.reserved_volume) > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {' '}(в резерве {Number(m.reserved_volume)} л)
                </Text>
              )}
              {m.onec_remaining_liters != null && (
                <>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    по 1С: {Number(m.onec_remaining_liters)} л
                  </Text>
                </>
              )}
            </span>
          ),
        },
        {
          title: '',
          key: 'action',
          width: 130,
          align: 'center',
          render: (_, m) => {
            if (m.already_in_document) return <Tag color="orange">уже в документе</Tag>;
            if (!m.selectable) return <Tag>недоступна</Tag>;
            return (
              <Button size="small" type="primary" onClick={() => onPick(m)}>
                Выбрать
              </Button>
            );
          },
        },
      ]}
    />
  );

  const title = 'Выбор марки (разливной товар)';
  if (isMobile) {
    return (
      <Drawer open={open} onClose={onCancel} title={title} placement="bottom" height="85%">
        {content}
      </Drawer>
    );
  }
  return (
    <Modal open={open} onCancel={onCancel} title={title} footer={null} width={720} centered>
      {content}
    </Modal>
  );
};

export default MarkPickerModal;
